import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { leagueAPI, clubAPI } from '../services/api';
import MapPicker from '../components/MapPicker';
import { useAlert } from '../components/Alert';
import { useConfirm } from '../components/Confirm';
import { useAuth } from '../context/AuthContext';
import './EditLeague.css';
import './CreateLeague.css';

const EditLeague = () => {
  const { leagueId } = useParams();
  const navigate = useNavigate();
  const { isAdmin: isGlobalAdmin } = useAuth();
  const [league, setLeague] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    sport: 'Tennis (singles)',
    minLevel: 0,
    maxLevel: 100,
    startDate: '',
    endDate: '',
    latitude: null,
    longitude: null,
    matchFormat: 'best-of-3-sets-to-6',
    isSuperLeague: false,
    maxPlayersPerSubLeague: 8,
    clubId: '',
  });
  const [initialFormData, setInitialFormData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { showAlert } = useAlert();
  const { showConfirm } = useConfirm();
  const [coverPhotoFile, setCoverPhotoFile] = useState(null);
  const [removeCoverPhoto, setRemoveCoverPhoto] = useState(false);
  const [clubs, setClubs] = useState([]);
  const [loadingClubs, setLoadingClubs] = useState(false);

  useEffect(() => {
    loadLeague();
  }, [leagueId, isGlobalAdmin]);

  useEffect(() => {
    // Load clubs where the current user is admin (for optional club selection)
    const loadClubs = async () => {
      setLoadingClubs(true);
      try {
        const res = await clubAPI.getMyClubs();
        const allClubs = res.data || [];
        const adminClubs = allClubs.filter((club) => club.isAdmin);
        setClubs(adminClubs);
      } catch (err) {
        console.error('Failed to load clubs for league edit:', err);
      } finally {
        setLoadingClubs(false);
      }
    };

    loadClubs();
  }, []);

  const loadLeague = async () => {
    try {
      // Check if this is a super league route
      const isSuperLeagueRoute = window.location.pathname.includes('/super-leagues/');
      
      let response;
      if (isSuperLeagueRoute) {
        response = await leagueAPI.getSuperLeague(leagueId);
      } else {
        response = await leagueAPI.getLeague(leagueId);
      }
      
      const leagueData = response.data;
      
      if (!leagueData.isAdmin && !isGlobalAdmin) {
        if (leagueData.isSuperLeague) {
          navigate(`/super-leagues/${leagueId}`);
        } else {
          navigate(`/leagues/${leagueId}`);
        }
        return;
      }

      // Prevent editing subleagues
      if (leagueData.superLeagueId) {
        navigate(`/leagues/${leagueId}`);
        return;
      }

      const viewerIsAdmin = leagueData.isAdmin || isGlobalAdmin;
      const normalizedLeagueData = {
        ...leagueData,
        isAdmin: viewerIsAdmin,
      };
      setLeague(normalizedLeagueData);
      const normalizedData = {
        name: normalizedLeagueData.name,
        location: normalizedLeagueData.location,
        sport: normalizedLeagueData.sport || 'Tennis (singles)',
        minLevel: normalizedLeagueData.minLevel,
        maxLevel: normalizedLeagueData.maxLevel,
        startDate: normalizedLeagueData.startDate || '',
        endDate: normalizedLeagueData.endDate || '',
        latitude: normalizedLeagueData.latitude || null,
        longitude: normalizedLeagueData.longitude || null,
        matchFormat: normalizedLeagueData.matchFormat || 'best-of-3-sets-to-6',
        isSuperLeague: Boolean(normalizedLeagueData.isSuperLeague),
        maxPlayersPerSubLeague: normalizedLeagueData.maxPlayersPerSubLeague ?? (normalizedLeagueData.isSuperLeague ? 8 : null),
        clubId: normalizedLeagueData.clubId || '',
      };
      setFormData(normalizedData);
      setInitialFormData({ ...normalizedData });
      setRemoveCoverPhoto(false);
      setCoverPhotoFile(null);
    } catch (err) {
      console.error('Failed to load league:', err);
      setError('Failed to load league');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updatedForm = { ...formData, [name]: value };
    
    // When sport changes, update match format to default for that sport
    if (name === 'sport') {
      if (value === 'Squash') {
        updatedForm.matchFormat = 'squash-best-of-5-games-to-11';
      } else if (!formData.matchFormat.startsWith('squash-')) {
        // Only change if current format is not squash-related
        updatedForm.matchFormat = 'best-of-3-sets-to-6';
      }
    }
    
    setFormData(updatedForm);
  };

  const handleClubChange = (e) => {
    const { value } = e.target;
    const selectedClubId = value || '';
    const selectedClub = clubs.find((c) => c.clubId === selectedClubId);

    setFormData((prev) => {
      // When a club with coordinates is selected, move league location and map pin
      // to the club's coordinates, and set the league location name to the club name.
      if (
        selectedClub &&
        typeof selectedClub.latitude === 'number' &&
        typeof selectedClub.longitude === 'number'
      ) {
        return {
          ...prev,
          clubId: selectedClubId,
          location: selectedClub.name || prev.location,
          latitude: selectedClub.latitude,
          longitude: selectedClub.longitude,
        };
      }
      return {
        ...prev,
        clubId: selectedClubId,
      };
    });
  };

  const handleDateChange = (e) => {
    handleChange(e);
    if (typeof e.target?.blur === 'function') {
      requestAnimationFrame(() => e.target.blur());
    }
  };

  const handleLocationSelect = (position) => {
    setFormData({
      ...formData,
      latitude: position.lat,
      longitude: position.lng,
    });
  };

  const handleCoverPhotoChange = (e) => {
    const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    setCoverPhotoFile(file);
    if (file) {
      setRemoveCoverPhoto(false);
    }
  };

  const editConfig = useMemo(() => {
    const allFields = [
      'name',
      'location',
      'sport',
      'startDate',
      'endDate',
      'minLevel',
      'maxLevel',
      'matchFormat',
      'maxPlayersPerSubLeague',
      'latitude',
      'longitude',
      'coverPhoto',
      'clubId',
    ];
    const allowedFields = new Set(allFields);
    let disabledMessage = '';

    if (!league) {
      return {
        allowedFields,
        disabledMessage,
        hasMatches: false,
        hasStarted: false,
        hasEnded: false,
      };
    }

    const now = new Date();
    const start = league.startDate ? new Date(league.startDate) : null;
    const end = league.endDate ? new Date(league.endDate) : null;
    const hasStarted = start ? now >= start : false;
    const hasEnded = end ? now > end : false;
    const hasMatches = Boolean(league.matchCount > 0 || league.hasMatches);

    if (hasMatches && hasStarted) {
      if (hasEnded) {
        allowedFields.clear();
        allowedFields.add('name');
        allowedFields.add('coverPhoto');
        disabledMessage = 'This league has concluded; only the name and cover photo can be edited.';
      } else {
        allowedFields.clear();
        allowedFields.add('name');
        allowedFields.add('endDate');
        allowedFields.add('coverPhoto');
        disabledMessage = 'Matches are in progress; only the league name, cover photo, and end date can be edited.';
      }
    }

    return {
      allowedFields,
      disabledMessage,
      hasMatches,
      hasStarted,
      hasEnded,
    };
  }, [league]);

  const isFieldEditable = useCallback(
    (field) => editConfig.allowedFields.has(field),
    [editConfig]
  );

  const handleDisabledFieldClick = useCallback(
    (field) => {
      if (isFieldEditable(field)) {
        return;
      }
      const message =
        editConfig.disabledMessage ||
        'This field cannot be edited at this time because matches have already been played.';
      showAlert(message, 'info');
    },
    [editConfig, isFieldEditable, showAlert]
  );

  const renderDisabledOverlay = (field) => {
    if (isFieldEditable(field)) {
      return null;
    }
    return (
      <button
        type="button"
        className="field-locked-overlay"
        onClick={() => handleDisabledFieldClick(field)}
        aria-label={`${field} is locked`}
      />
    );
  };

  const renderDisabledNote = useCallback(
    (field) => {
      if (isFieldEditable(field)) {
        return null;
      }
      if (editConfig.hasEnded) {
        return (
          <small className="form-hint">
            This property cannot be edited after a league is completed.
          </small>
        );
      }
      return null;
    },
    [isFieldEditable, editConfig]
  );

  const isMapEditable =
    isFieldEditable('latitude') &&
    isFieldEditable('longitude');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.name) {
      showAlert('Name is required', 'warning');
      return;
    }

    const minLevel = parseInt(formData.minLevel, 10);
    const maxLevel = parseInt(formData.maxLevel, 10);
    const maxPlayersPerSubLeague =
      formData.maxPlayersPerSubLeague !== null && formData.maxPlayersPerSubLeague !== undefined && formData.maxPlayersPerSubLeague !== ''
        ? parseInt(formData.maxPlayersPerSubLeague, 10)
        : null;

    if (Number.isNaN(minLevel) || minLevel < 0) {
      showAlert('Min level must be a number of at least 0', 'warning');
      return;
    }

    if (Number.isNaN(maxLevel) || maxLevel > 100) {
      showAlert('Max level must be a number of at most 100', 'warning');
      return;
    }

    if (minLevel >= maxLevel) {
      showAlert('Min level must be less than max level', 'warning');
      return;
    }

    if (formData.startDate && formData.endDate && new Date(formData.startDate) > new Date(formData.endDate)) {
      showAlert('Start date cannot be after end date', 'warning');
      return;
    }

    if (formData.isSuperLeague) {
      if (Number.isNaN(maxPlayersPerSubLeague)) {
        showAlert('Max players per subleague must be a number', 'warning');
        return;
      }

      if (maxPlayersPerSubLeague < 2) {
        showAlert('Max players per subleague is required and must be at least 2 for super leagues', 'warning');
        return;
      }
    } else if (
      formData.maxPlayersPerSubLeague !== null &&
      formData.maxPlayersPerSubLeague !== undefined &&
      formData.maxPlayersPerSubLeague !== ''
    ) {
      if (Number.isNaN(maxPlayersPerSubLeague)) {
        showAlert('Max players per subleague must be a number', 'warning');
        return;
      }

      if (maxPlayersPerSubLeague < 2) {
        showAlert('Max players per subleague must be at least 2', 'warning');
        return;
      }
    }

    setSaving(true);

    try {
      const diffPayload = {};
      const initialData = initialFormData || {};

      const toInt = (value) => {
        if (value === '' || value === null || value === undefined) return null;
        const num = parseInt(value, 10);
        return Number.isNaN(num) ? null : num;
      };

      const toFloat = (value) => {
        if (value === '' || value === null || value === undefined) return null;
        const num = parseFloat(value);
        return Number.isNaN(num) ? null : num;
      };

      const addStringField = (key) => {
        if (!isFieldEditable(key)) return;
        const currentValue = formData[key] ?? '';
        const initialValue = initialData[key] ?? '';
        if (!initialFormData || currentValue !== initialValue) {
          diffPayload[key] = currentValue;
        }
      };

      const addDateField = (key) => {
        if (!isFieldEditable(key)) return;
        const currentValue = formData[key] || null;
        const initialValue = initialData[key] || null;
        if (!initialFormData || currentValue !== initialValue) {
          diffPayload[key] = currentValue;
        }
      };

      const addIntField = (key) => {
        if (!isFieldEditable(key)) return;
        const currentValue = toInt(formData[key]);
        const initialValue = toInt(initialData[key]);
        const bothNullish = (currentValue === null || currentValue === undefined) && (initialValue === null || initialValue === undefined);
        if (!initialFormData || (!bothNullish && currentValue !== initialValue)) {
          if (!initialFormData && currentValue === null) return;
          diffPayload[key] = currentValue;
        }
      };

      const addFloatField = (key) => {
        if (!isFieldEditable(key)) return;
        const currentValue = toFloat(formData[key]);
        const initialValue = toFloat(initialData[key]);
        const bothNullish = (currentValue === null || currentValue === undefined) && (initialValue === null || initialValue === undefined);
        if (!initialFormData || (!bothNullish && currentValue !== initialValue)) {
          if (!initialFormData && currentValue === null) return;
          diffPayload[key] = currentValue;
        }
      };

      addStringField('name');
      addStringField('location');
      addStringField('sport');
      addDateField('startDate');
      addDateField('endDate');
      addIntField('minLevel');
      addIntField('maxLevel');
      addFloatField('latitude');
      addFloatField('longitude');
      addStringField('matchFormat');
      addStringField('clubId');

      if (formData.isSuperLeague || (initialData.maxPlayersPerSubLeague !== null && initialData.maxPlayersPerSubLeague !== undefined)) {
        addIntField('maxPlayersPerSubLeague');
      }

      const coverPhotoEditable = isFieldEditable('coverPhoto');
      const coverPhotoRemoved = coverPhotoEditable && removeCoverPhoto;
      if (coverPhotoRemoved) {
        diffPayload.coverPhotoKey = null;
        diffPayload.coverPhotoUrl = null;
      }

      let updatedSomething = false;

      if (Object.keys(diffPayload).length > 0) {
        await leagueAPI.updateLeague(leagueId, diffPayload);
        updatedSomething = true;
        setInitialFormData((prev) => {
          if (!prev) {
            return { ...formData };
          }
          return { ...prev, ...diffPayload };
        });
        setLeague((prev) => (prev ? { ...prev, ...diffPayload } : prev));
        if (coverPhotoRemoved) {
          setRemoveCoverPhoto(false);
        }
      }

      if (coverPhotoFile) {
        try {
          const uploadRes = await (await import('../services/api')).uploadAPI.uploadLeagueLogo(coverPhotoFile, leagueId);
          await leagueAPI.updateLeague(leagueId, {
            coverPhotoKey: uploadRes.data.key,
            coverPhotoUrl: uploadRes.data.url,
          });
          setCoverPhotoFile(null);
          setLeague((prev) => (prev ? { ...prev, coverPhotoKey: uploadRes.data.key, coverPhotoUrl: uploadRes.data.url } : prev));
          updatedSomething = true;
        } catch (uploadErr) {
          console.error('Cover photo upload failed:', uploadErr);
          showAlert(uploadErr.response?.data?.error || 'League saved, but cover photo upload failed', 'warning');
        }
      }

      if (!updatedSomething) {
        setSaving(false);
        showAlert('No changes to save', 'info');
        return;
      }

    setSuccess('League updated successfully');
      const isSuperLeague = league?.isSuperLeague || window.location.pathname.includes('/super-leagues/');
      setTimeout(() => {
        if (isSuperLeague) {
          navigate(`/super-leagues/${leagueId}`);
        } else {
          navigate(`/leagues/${leagueId}`);
        }
      }, 1500);
    } catch (err) {
      console.error('Update league error:', err);
      const serverMsg = err.response?.data?.error || 'Failed to update league';
      const blocked = err.response?.data?.blockedFields;
      const details = Array.isArray(blocked) && blocked.length > 0 ? `\nBlocked fields: ${blocked.join(', ')}` : '';
      const message = `${serverMsg}${details}`;
      // Use app-wide alert popup for server errors (warning for 400, error otherwise)
      const status = err.response?.status;
      if (status === 400) {
        showAlert(message, 'warning');
      } else {
        showAlert(message, 'error');
      }
      // Avoid duplicating the inline error banner for server-side errors
      setError('');
    } finally {
      setSaving(false);
    }
  };

  const hasApprovedPlayers = () => {
    if (!league) return false;
    const players = Array.isArray(league.players) ? league.players : [];
    // On API, players are full objects here; compare userId with createdBy
    return players.some(p => (p?.userId || p) !== league.createdBy);
  };

  const handleDelete = () => {
    if (!league) return;
    const disabled = hasApprovedPlayers();
    if (disabled) {
      showAlert('Cannot delete this league: there are approved players.', 'warning');
      return;
    }
    showConfirm('Are you sure you want to delete this league? This action cannot be undone.', async () => {
      try {
        await leagueAPI.deleteLeague(leagueId);
        showAlert('League deleted successfully', 'success');
        navigate('/leagues');
      } catch (err) {
        console.error('Delete league error:', err);
        showAlert(err.response?.data?.error || 'Failed to delete league', 'error');
      }
    });
  };

  const handleRemoveCover = () => {
    if (!league) return;
    if (!isFieldEditable('coverPhoto')) {
      handleDisabledFieldClick('coverPhoto');
      return;
    }
    setRemoveCoverPhoto(true);
    setCoverPhotoFile(null);
  };

  const handleCancelRemoveCover = () => {
    setRemoveCoverPhoto(false);
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  if (error && !league) {
    return (
      <div className="page-container">
        <div className="container">
          <div className="error-message">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="container">
        <div className="page-header">
          <h1 className="page-title">{league?.isSuperLeague ? 'Edit Super League' : 'Edit League'}</h1>
          <p className="page-subtitle">Update league details, requirements, and settings</p>
        </div>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <div className="create-league-card card">
          <form onSubmit={handleSubmit} className="create-league-form">
            <div className="form-row three-columns">
              <div className="form-group">
                <label className="form-label">League Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`form-input ${isFieldEditable('name') ? '' : 'locked-field'}`}
                  placeholder="League name"
                />
                {renderDisabledOverlay('name')}
              </div>

              <div className={`form-group ${!isFieldEditable('sport') ? 'field-locked' : ''}`}>
                <label className="form-label">Sport</label>
                <div className="field-input-wrapper">
                  <select
                    name="sport"
                    value={formData.sport}
                    onChange={handleChange}
                    className={`form-input ${isFieldEditable('sport') ? '' : 'locked-field'}`}
                    disabled={!isFieldEditable('sport')}
                  >
                    <option value="Tennis (singles)">🎾 Tennis (singles)</option>
                    <option value="Tennis (doubles)" disabled>🎾 Tennis (doubles) (Coming Soon)</option>
                    <option value="Padel" disabled>🏓 Padel (Coming Soon)</option>
                    <option value="Squash">🏸 Squash</option>
                    <option value="Football" disabled>⚽ Football (Coming Soon)</option>
                    <option value="Basketball" disabled>🏀 Basketball (Coming Soon)</option>
                  </select>
                  {renderDisabledOverlay('sport')}
                </div>
                {renderDisabledNote('sport')}
              </div>

              <div className={`form-group ${!isFieldEditable('coverPhoto') ? 'field-locked' : ''}`}>
                <label className="form-label">League Logo</label>
                {league?.coverPhotoUrl && !removeCoverPhoto && (
                  <div className="cover-preview">
                    <img
                      src={league.coverPhotoUrl}
                      alt="Current league logo"
                      className="cover-preview-image"
                    />
                    {isFieldEditable('coverPhoto') && (
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleRemoveCover}
                      >
                        Remove Current Logo
                      </button>
                    )}
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  className="form-input"
                  onChange={handleCoverPhotoChange}
                  disabled={!isFieldEditable('coverPhoto')}
                />
                {coverPhotoFile && (
                  <small className="form-hint">
                    Selected file: {coverPhotoFile.name}{' '}
                    {isFieldEditable('coverPhoto') && (
                      <button
                        type="button"
                        className="inline-clear-button"
                        onClick={() => setCoverPhotoFile(null)}
                      >
                        Clear
                      </button>
                    )}
                  </small>
                )}
                {removeCoverPhoto && (
                  <small className="form-hint">
                    Logo will be removed after you save changes.{' '}
                    <button
                      type="button"
                      className="inline-clear-button"
                      onClick={handleCancelRemoveCover}
                    >
                      Undo
                    </button>
                  </small>
                )}
                <small className="form-hint">
                  Upload a square image for best results. This logo appears on league cards and pages.
                </small>
                {renderDisabledOverlay('coverPhoto')}
              </div>
            </div>

            <div className="form-row two-columns">
              <div className={`form-group ${!isFieldEditable('startDate') ? 'field-locked' : ''}`}>
                <label className="form-label">Start Date *</label>
                <div className="field-input-wrapper">
                  <input
                    type="date"
                    name="startDate"
                    className="form-input"
                    value={formData.startDate}
                    onChange={handleDateChange}
                    disabled={!isFieldEditable('startDate')}
                    required
                  />
                  {renderDisabledOverlay('startDate')}
                </div>
                {renderDisabledNote('startDate')}
              </div>

              <div className={`form-group ${!isFieldEditable('endDate') ? 'field-locked' : ''}`}>
                <label className="form-label">End Date *</label>
                <div className="field-input-wrapper">
                  <input
                    type="date"
                    name="endDate"
                    className="form-input"
                    value={formData.endDate}
                    onChange={handleDateChange}
                    disabled={!isFieldEditable('endDate')}
                    required
                  />
                  {renderDisabledOverlay('endDate')}
                </div>
                {renderDisabledNote('endDate')}
              </div>
            </div>

            <div className={`form-group ${!isFieldEditable('clubId') ? 'field-locked' : ''}`}>
              <label className="form-label">Club (optional)</label>
              <div className="field-input-wrapper">
                <select
                  name="clubId"
                  className="form-input"
                  value={formData.clubId || ''}
                  onChange={handleClubChange}
                  disabled={!isFieldEditable('clubId') || loadingClubs}
                >
                  <option value="">No club (public league)</option>
                  {clubs.map((club) => (
                    <option key={club.clubId} value={club.clubId}>
                      {club.name}
                    </option>
                  ))}
                </select>
                {renderDisabledOverlay('clubId')}
              </div>
              <small className="form-hint">
                Change or clear the club this league is grouped under.
              </small>
            </div>

            <div className={`form-group ${!isMapEditable ? 'field-locked map-locked' : ''}`}>
              <label className="form-label">Select Location on Map *</label>
              <MapPicker
                onLocationSelect={handleLocationSelect}
                initialLocation={
                  formData.latitude !== null && formData.longitude !== null
                    ? { lat: parseFloat(formData.latitude), lng: parseFloat(formData.longitude) }
                    : null
                }
                readOnly={!isMapEditable}
              />
              {!isMapEditable && (
                <button
                  type="button"
                  className="field-locked-overlay"
                  onClick={() => handleDisabledFieldClick('location')}
                  aria-label="Location selection is locked"
                />
              )}
            </div>

            <div className="form-section">
              <h2>Player & Level Settings</h2>
              <div className="form-grid">
                <div className={`form-group ${!isFieldEditable('minLevel') ? 'field-locked' : ''}`}>
                  <label className="form-label">Min Level</label>
                  <div className="field-input-wrapper">
                    <input
                      type="number"
                      name="minLevel"
                      value={formData.minLevel}
                      onChange={handleChange}
                      min="0"
                      max="100"
                      className={`form-input ${isFieldEditable('minLevel') ? '' : 'locked-field'}`}
                    />
                    {renderDisabledOverlay('minLevel')}
                  </div>
                  {renderDisabledNote('minLevel')}
                </div>

                <div className={`form-group ${!isFieldEditable('maxLevel') ? 'field-locked' : ''}`}>
                  <label className="form-label">Max Level</label>
                  <div className="field-input-wrapper">
                    <input
                      type="number"
                      name="maxLevel"
                      value={formData.maxLevel}
                      onChange={handleChange}
                      min="0"
                      max="100"
                      className={`form-input ${isFieldEditable('maxLevel') ? '' : 'locked-field'}`}
                    />
                    {renderDisabledOverlay('maxLevel')}
                  </div>
                  {renderDisabledNote('maxLevel')}
                </div>
              </div>
            </div>

            <div className="form-section-title">League Settings</div>

            <div className="form-row two-columns">
              <div className="form-group">
                <label className="form-label">League Type</label>
                <select
                  name="leagueType"
                  className="form-input"
                  value={formData.isSuperLeague ? 'super' : 'individual'}
                  disabled
                >
                  <option value="individual">Individual League</option>
                  <option value="super">Super League</option>
                  <option value="tournament">Knockout Tournament (Coming Soon)</option>
                </select>
                <small className="form-hint">
                  League type cannot be changed after creation
                </small>
              </div>

              <div className={`form-group ${!isFieldEditable('matchFormat') ? 'field-locked' : ''}`}>
                <label className="form-label">Match Format *</label>
                <div className="field-input-wrapper">
                  <select
                    name="matchFormat"
                    className="form-input"
                    value={formData.matchFormat}
                    onChange={handleChange}
                    disabled={!isFieldEditable('matchFormat')}
                    required
                  >
                    {formData.sport === 'Squash' ? (
                      <>
                        <option value="squash-best-of-5-games-to-11">Best of 5 Games (First to 11, win by 2)</option>
                        <option value="squash-best-of-3-games-to-11">Best of 3 Games (First to 11, win by 2)</option>
                      </>
                    ) : (
                      <>
                        <option value="best-of-3-sets-to-6">Best of 3 Sets (Sets to 6)</option>
                        <option value="best-of-3-sets-to-4">Best of 3 Sets (Sets to 4)</option>
                        <option value="best-of-3-deciding-tiebreak-sets-to-6">Best of 3 with 3rd Set Tiebreak to 10 (Sets to 6)</option>
                        <option value="best-of-3-deciding-tiebreak-sets-to-4">Best of 3 with 3rd Set Tiebreak to 10 (Sets to 4)</option>
                        <option value="single-set">Single Set</option>
                      </>
                    )}
                  </select>
                  {renderDisabledOverlay('matchFormat')}
                </div>
                {renderDisabledNote('matchFormat')}
                {!renderDisabledNote('matchFormat') && (
                  <small className="form-hint">
                    This format will be used for all matches in the league
                  </small>
                )}
              </div>
            </div>

            {formData.isSuperLeague && (
              <div className={`form-group ${!isFieldEditable('maxPlayersPerSubLeague') ? 'field-locked' : ''}`}>
                <label className="form-label">Max Players per Subleague</label>
                <div className="field-input-wrapper">
                  <input
                    type="number"
                    name="maxPlayersPerSubLeague"
                    value={formData.maxPlayersPerSubLeague}
                    onChange={handleChange}
                    min="2"
                    max="50"
                    className={`form-input ${isFieldEditable('maxPlayersPerSubLeague') ? '' : 'locked-field'}`}
                  />
                  {renderDisabledOverlay('maxPlayersPerSubLeague')}
                </div>
                {renderDisabledNote('maxPlayersPerSubLeague')}
                {!renderDisabledNote('maxPlayersPerSubLeague') && (
                  <small className="form-hint">Players will be automatically split into subleagues up to this size.</small>
                )}
              </div>
            )}

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                type="button"
                onClick={() => {
                  const isSuperLeague = league?.isSuperLeague || window.location.pathname.includes('/super-leagues/');
                  if (isSuperLeague) {
                    navigate(`/super-leagues/${leagueId}`);
                  } else {
                    navigate(`/leagues/${leagueId}`);
                  }
                }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>

        <div className="card" style={{ borderColor: '#dc2626' }}>
          <h2>Danger Zone</h2>
          <p style={{ marginTop: 0 }}>Delete this {league?.isSuperLeague ? 'Super League' : 'League'}.</p>
          <button
            type="button"
            className="btn btn-danger"
            onClick={handleDelete}
            disabled={hasApprovedPlayers()}
            title={hasApprovedPlayers() ? 'Cannot delete while there are approved players' : ''}
          >
            Delete {league?.isSuperLeague ? 'Super League' : 'League'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditLeague;


